import React, { useState } from 'react';
import { Upload, Pause, CheckCircle } from 'lucide-react';
import Button from './Button';
import FileUpload from './FileUpload';
import { useFileApi } from '../../hooks/useFileApi';
import { getContainerName, ComponentContainerMap } from '../../constants/ComponentConstants';
import { FileTypeConstants } from '../../constants/FileTypeConstants';

export interface ProofUploadProps {
  componentId?: string;
  entityId?: string;
  memberId?: string;
  isPending?: boolean;
  isUploaded?: boolean;
  onUploadSuccess: (fileRecordId: string) => void | Promise<void>;
  onUploadLater?: () => void;
  title?: string;
  accept?: string[];
}

const ProofUpload: React.FC<ProofUploadProps> = ({
  componentId,
  entityId,
  memberId,
  isPending = false,
  isUploaded = false,
  onUploadSuccess,
  onUploadLater,
  title = 'Upload Proof of Payment',
  accept = FileTypeConstants.ALL_SUPPORTED,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadComplete, setUploadComplete] = useState(isUploaded);

  const { uploadFile, isLoading } = useFileApi();

  // Resolve container name from containerName prop or componentId
  const resolveContainerName = (): string => {
    if (componentId && ComponentContainerMap[componentId]) {
      return getContainerName(componentId);
    }
    throw new Error('Either componentId or containerName must be provided');
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setError(null);
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
    }
  };

  const handleUploadNow = async (): Promise<void> => {
    if (!file) {
      setError('Please select a file to upload');
      return;
    }

    setError(null);
    setUploading(true);

    try {
      const container = resolveContainerName();
      
      const response = await uploadFile(
        container,
        file.name,
        file,
        'members',
      );

      if (response?.success && response.data) {
        await onUploadSuccess(response.data);
        setUploadComplete(true);
      } else {
        throw new Error(response?.message || 'Failed to upload file');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred while uploading';
      setError(errorMessage);
      console.error('Proof of payment upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleUploadLater = (): void => {
    onUploadLater?.();
  };

  const loading = uploading || isLoading;

  return (
    <div className="flex flex-col space-y-4">
      <div className="mb-4">
        <FileUpload
          id="proofOfPayment-upload"
          title="Upload proof of payment."
          accept={accept}
          onChange={handleFileChange}
          error={error || ''}
        />
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
        <Button
          className="w-full text-sm"
          disabled={loading || !file}
          onClick={handleUploadNow}
          icon={Upload}
        >
          {loading ? 'Uploading...' : 'Upload Now'}
        </Button>

        {onUploadLater && (
          <Button
            className="w-full text-sm"
            disabled={loading}
            onClick={handleUploadLater}
            icon={Pause}
          >
            Upload Later
          </Button>
        )}
      </div>
    </div>
  );
};

export default ProofUpload;